/**
 * Secure authentication service for Office Add-ins
 * Manages JWT tokens and authentication with Carousel API
 *
 * PERSISTENCE STRATEGY:
 * - Uses localStorage for persistence across add-in close/reopen
 * - Uses Office.context.partitionKey when available for secure isolation
 *   with fallback to unpartitioned keys for cross-context restore
 * - Tokens persist globally (not per-document) so users stay logged in
 * - Automatically refreshes tokens on 401/403 responses
 *
 * ⚠️ IMPORTANT: Auth tokens persist across add-in restarts by design.
 * Users remain logged in even after closing/reopening PowerPoint.
 * To clear auth on startup for testing, uncomment the clearOnStartup() call in initialize().
 */
class AuthService {
    constructor() {
        // In-memory storage + localStorage persistence
        this.currentTokens = null;
        this.currentUser = null;
        this.authChangeHandlers = new Set();
        this.isInitialized = false;
        // API configuration
        this.apiBaseUrl = 'https://app.carouseltech.io';
        this.tokenRefreshBuffer = 5 * 60 * 1000; // 5 minutes before expiry
        // Base storage keys - will be prefixed with partition key if available
        this.BASE_STORAGE_KEYS = {
            TOKENS: 'carousel_auth_tokens',
            USER: 'carousel_user_info'
        };
        console.log('[AuthService] Initialized');
    }
    getCurrentPartitionKey() {
        try {
            const partitionKey = typeof Office !== "undefined" ? Office.context?.partitionKey : undefined;
            if (typeof partitionKey === "string" && partitionKey.length > 0) {
                return partitionKey;
            }
        }
        catch {
            // Office not available; fall back to base key.
        }
        return undefined;
    }
    getPartitionedStorageKey(baseKey, partitionKey) {
        const resolvedPartitionKey = partitionKey ?? this.getCurrentPartitionKey();
        if (!resolvedPartitionKey) {
            return null;
        }
        return `${resolvedPartitionKey}_${baseKey}`;
    }
    /**
     * Read order: partitioned key first, then unpartitioned fallback.
     */
    getReadStorageKeys(baseKey) {
        const keys = new Set();
        const partitionedKey = this.getPartitionedStorageKey(baseKey);
        if (partitionedKey) {
            keys.add(partitionedKey);
        }
        keys.add(baseKey);
        return Array.from(keys);
    }
    /**
     * Write to both partitioned and unpartitioned keys so auth survives
     * runtime/document partition changes.
     */
    getWriteStorageKeys(baseKey) {
        const keys = new Set();
        const partitionedKey = this.getPartitionedStorageKey(baseKey);
        if (partitionedKey) {
            keys.add(partitionedKey);
        }
        keys.add(baseKey);
        return Array.from(keys);
    }
    readFromStorageWithFallback(baseKey) {
        const readKeys = this.getReadStorageKeys(baseKey);
        for (const key of readKeys) {
            const value = localStorage.getItem(key);
            if (value) {
                return { value, sourceKey: key };
            }
        }
        return { value: null, sourceKey: null };
    }
    /**
     * Get all possible storage keys (with and without partition) for complete cleanup
     */
    getAllStorageKeys(baseKey) {
        const keys = new Set(this.getWriteStorageKeys(baseKey));
        try {
            // Include stale keys from previous Office partition keys.
            const partitionedSuffix = `_${baseKey}`;
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.endsWith(partitionedSuffix)) {
                    keys.add(key);
                }
            }
        }
        catch {
            // Office not available
        }
        return Array.from(keys);
    }
    static getInstance() {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }
    /**
     * Clear all stored auth on startup (for testing/development)
     * Uncomment the call to this in initialize() to force fresh login each session.
     */
    clearOnStartup() {
        console.log('[AuthService] ⚠️ clearOnStartup() called - clearing all stored auth');
        this.clearStoredAuth();
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }
    /**
     * Initialize auth service - call this on app startup
     * Attempts to restore auth from localStorage and validate/refresh if needed
     * Returns true if user is authenticated after initialization
     */
    async initialize() {
        if (this.isInitialized) {
            return this.isAuthenticated();
        }
        console.log('[AuthService] Initializing...');
        // ⚠️ UNCOMMENT TO CLEAR AUTH ON EVERY STARTUP (for testing):
        // this.clearOnStartup();
        try {
            // Try to restore from localStorage
            await this.restoreFromStorage();
            // If we have tokens, try to validate them
            if (this.currentTokens && !this.isTokenExpired()) {
                console.log('[AuthService] Found valid stored tokens');
                this.isInitialized = true;
                this.startTokenRefreshTimer();
                // Don't notify during initialization - let the app check isAuthenticated() itself
                return true;
            }
            // If access token expired but we have refresh token, try to refresh
            if (this.currentTokens?.refreshToken) {
                console.log('[AuthService] Access token expired, attempting refresh...');
                try {
                    await this.refreshAccessToken();
                    this.isInitialized = true;
                    return true;
                }
                catch (error) {
                    console.log('[AuthService] Token refresh failed, user needs to login');
                    this.clearStoredAuth();
                }
            }
            this.isInitialized = true;
            return false;
        }
        catch (error) {
            console.error('[AuthService] Error during initialization:', error);
            this.isInitialized = true;
            return false;
        }
    }
    /**
     * Reload auth state from storage (public method)
     * Call this when you know the storage has been updated externally
     */
    async reloadFromStorage() {
        await this.restoreFromStorage();
    }
    /**
     * Restore auth state from localStorage
     * Uses localStorage for global persistence across all presentations
     */
    async restoreFromStorage() {
        try {
            const { value: tokensJson, sourceKey: tokensSourceKey, } = this.readFromStorageWithFallback(this.BASE_STORAGE_KEYS.TOKENS);
            const { value: userJson, sourceKey: userSourceKey, } = this.readFromStorageWithFallback(this.BASE_STORAGE_KEYS.USER);
            // Track if auth state changed
            const wasAuthenticated = !!this.currentTokens && !!this.currentUser;
            const previousEmail = this.currentUser?.email;
            if (tokensJson) {
                const tokens = JSON.parse(tokensJson);
                this.currentTokens = {
                    ...tokens,
                    expiresAt: new Date(tokens.expiresAt)
                };
                console.log('[AuthService] ✅ Restored tokens from localStorage', {
                    sourceKey: tokensSourceKey,
                    email: tokens.userEmail,
                    expiresAt: this.currentTokens.expiresAt.toISOString(),
                    isExpired: new Date() >= this.currentTokens.expiresAt
                });
                // If fallback base key was used, migrate into current partition key.
                const partitionedTokensKey = this.getPartitionedStorageKey(this.BASE_STORAGE_KEYS.TOKENS);
                if (partitionedTokensKey &&
                    tokensSourceKey === this.BASE_STORAGE_KEYS.TOKENS &&
                    !localStorage.getItem(partitionedTokensKey)) {
                    localStorage.setItem(partitionedTokensKey, tokensJson);
                    console.log("[AuthService] Migrated tokens from base key to partitioned key");
                }
            }
            else {
                this.currentTokens = null;
                console.log('[AuthService] ❌ No tokens found in localStorage');
            }
            if (userJson) {
                this.currentUser = JSON.parse(userJson);
                console.log('[AuthService] ✅ Restored user from localStorage:', {
                    sourceKey: userSourceKey,
                    email: this.currentUser.email,
                });
                // If fallback base key was used, migrate into current partition key.
                const partitionedUserKey = this.getPartitionedStorageKey(this.BASE_STORAGE_KEYS.USER);
                if (partitionedUserKey &&
                    userSourceKey === this.BASE_STORAGE_KEYS.USER &&
                    !localStorage.getItem(partitionedUserKey)) {
                    localStorage.setItem(partitionedUserKey, userJson);
                    console.log("[AuthService] Migrated user info from base key to partitioned key");
                }
            }
            else {
                this.currentUser = null;
                console.log('[AuthService] ❌ No user found in localStorage');
            }
            // Only notify if auth state actually changed (and not during initial load)
            const isNowAuthenticated = !!this.currentTokens && !this.isTokenExpired();
            const currentEmail = this.currentUser?.email;
            if (this.isInitialized && (wasAuthenticated !== isNowAuthenticated || previousEmail !== currentEmail)) {
                console.log('[AuthService] Auth state changed:', {
                    from: wasAuthenticated ? previousEmail : 'not authenticated',
                    to: isNowAuthenticated ? currentEmail : 'not authenticated'
                });
                this.notifyAuthChange(isNowAuthenticated);
            }
        }
        catch (error) {
            console.error('[AuthService] Error restoring from localStorage:', error);
        }
    }
    /**
     * Clean up old or orphaned carousel data from localStorage
     * This helps free up space when hitting quota limits
     */
    cleanupOldStorageData() {
        try {
            console.log('[AuthService] Cleaning up old storage data...');
            const keysToRemove = [];
            // Find all carousel-related keys
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.includes('carousel_')) {
                    keysToRemove.push(key);
                }
            }
            // Remove all carousel keys (we'll re-save the current ones)
            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                console.log('[AuthService] Cleaned up key:', key);
            });
            console.log(`[AuthService] Cleaned up ${keysToRemove.length} old keys`);
        }
        catch (error) {
            console.error('[AuthService] Error during storage cleanup:', error);
        }
    }
    /**
     * Save auth state to localStorage
     * Uses partition key for secure isolation when available
     * Implements quota error handling with automatic cleanup and retry
     */
    saveToStorage() {
        const saveAttempt = (isRetry = false) => {
            try {
                const tokenKeys = this.getWriteStorageKeys(this.BASE_STORAGE_KEYS.TOKENS);
                const userKeys = this.getWriteStorageKeys(this.BASE_STORAGE_KEYS.USER);
                if (this.currentTokens) {
                    const tokensJson = JSON.stringify({
                        accessToken: this.currentTokens.accessToken,
                        refreshToken: this.currentTokens.refreshToken,
                        expiresAt: this.currentTokens.expiresAt.toISOString(),
                        userEmail: this.currentTokens.userEmail
                    });
                    tokenKeys.forEach((key) => localStorage.setItem(key, tokensJson));
                    console.log('[AuthService] ✅ Saved tokens to localStorage' + (isRetry ? ' (after cleanup)' : ''), {
                        keys: tokenKeys,
                    });
                }
                else {
                    tokenKeys.forEach((key) => localStorage.removeItem(key));
                }
                const userJson = this.currentUser ? JSON.stringify(this.currentUser) : null;
                if (this.currentUser) {
                    userKeys.forEach((key) => localStorage.setItem(key, userJson));
                }
                else {
                    userKeys.forEach((key) => localStorage.removeItem(key));
                }
                return true;
            }
            catch (error) {
                if (error?.name === 'QuotaExceededError' && !isRetry) {
                    console.warn('[AuthService] ⚠️ localStorage quota exceeded, attempting cleanup and retry...');
                    return false; // Signal that we should retry
                }
                console.error('[AuthService] ❌ Error saving to localStorage:', error);
                return false;
            }
        };
        // First attempt
        const firstAttemptSuccess = saveAttempt(false);
        if (!firstAttemptSuccess) {
            // Clean up old data and retry
            this.cleanupOldStorageData();
            const retrySuccess = saveAttempt(true);
            if (!retrySuccess) {
                console.warn('[AuthService] ⚠️ Could not save to localStorage even after cleanup.');
                console.warn('[AuthService] ⚠️ Auth will work in-memory but will not persist after add-in restart.');
                console.warn('[AuthService] ℹ️ You may need to clear your browser cache or localStorage manually if this persists.');
            }
        }
    }
    /**
     * Clear stored auth data from localStorage
     * Clears all possible storage keys (with and without partition) for complete cleanup
     */
    clearStoredAuth() {
        try {
            // Get all possible storage keys for tokens and user
            const allTokenKeys = this.getAllStorageKeys(this.BASE_STORAGE_KEYS.TOKENS);
            const allUserKeys = this.getAllStorageKeys(this.BASE_STORAGE_KEYS.USER);
            // Remove all variations of tokens
            allTokenKeys.forEach(key => {
                localStorage.removeItem(key);
                console.log('[AuthService] Removed key:', key);
            });
            // Remove all variations of user info
            allUserKeys.forEach(key => {
                localStorage.removeItem(key);
                console.log('[AuthService] Removed key:', key);
            });
            // Clear in-memory state
            this.currentTokens = null;
            this.currentUser = null;
            console.log('[AuthService] Cleared stored auth from all storage keys');
        }
        catch (error) {
            console.error('[AuthService] Error clearing stored auth:', error);
        }
    }
    /**
     * Login with email and password
     */
    async login(email, password) {
        try {
            console.log(`[AuthService] Attempting login for ${email}...`);
            // Clear any existing auth (but don't notify yet)
            this.clearStoredAuth();
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
                this.refreshTimer = undefined;
            }
            // Prepare form data for OAuth2 password flow
            const formData = new URLSearchParams();
            formData.append('username', email);
            formData.append('password', password);
            formData.append('grant_type', 'password');
            // Login request
            const response = await fetch(`${this.apiBaseUrl}/user/auth/sign-in`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData.toString()
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(`Login failed: ${error || response.statusText}`);
            }
            // Parse token response
            const tokenData = await response.json();
            // Store tokens in memory
            this.currentTokens = {
                accessToken: tokenData.access_token || tokenData,
                refreshToken: tokenData.refresh_token,
                expiresAt: this.calculateExpiry(tokenData.expires_in || 3600),
                userEmail: email
            };
            // Get user info
            await this.fetchUserInfo();
            // Save to localStorage for persistence
            this.saveToStorage();
            // Start refresh timer
            this.startTokenRefreshTimer();
            // Notify listeners
            console.log('[AuthService] Notifying listeners of auth change. Listener count:', this.authChangeHandlers.size);
            this.notifyAuthChange(true);
            console.log('[AuthService] Login successful - tokens persisted to localStorage');
            return this.currentUser;
        }
        catch (error) {
            console.error('[AuthService] Login error:', error);
            this.clearStoredAuth();
            this.notifyAuthChange(false);
            throw error;
        }
    }
    /**
     * Fetch user information using current token
     */
    async fetchUserInfo() {
        if (!this.currentTokens) {
            throw new Error('No authentication token available');
        }
        const response = await fetch(`${this.apiBaseUrl}/user/auth/info`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${this.currentTokens.accessToken}`
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to get user info: ${response.statusText}`);
        }
        const userData = await response.json();
        // Map API response fields to UserInfo interface
        // API returns _id (MongoDB), but we map it to id internally
        this.currentUser = {
            _id: userData._id,
            email: userData.email,
            firstName: userData.first_name,
            lastName: userData.last_name,
            companyName: userData.company_name,
            companyUuid: userData.company_uuid,
            plan: userData.plan,
            requestLimit: userData.request_limit,
            requestsUsed: userData.requests_used,
            renewalDate: userData.renewal_date,
        };
        console.log('[AuthService] User info fetched and mapped:', this.currentUser.email, 'id:', this.currentUser._id);
    }
    /**
     * Refresh the access token
     */
    async refreshAccessToken() {
        if (!this.currentTokens?.refreshToken) {
            throw new Error('No refresh token available');
        }
        console.log('[AuthService] Refreshing access token...');
        try {
            const response = await fetch(`${this.apiBaseUrl}/user/auth/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    refresh_token: this.currentTokens.refreshToken
                })
            });
            if (!response.ok) {
                throw new Error(`Token refresh failed: ${response.statusText}`);
            }
            const newToken = await response.json();
            // Update tokens
            this.currentTokens = {
                ...this.currentTokens,
                accessToken: newToken.access_token || newToken,
                // Also update refresh token if provided (some APIs rotate refresh tokens)
                refreshToken: newToken.refresh_token || this.currentTokens.refreshToken,
                expiresAt: this.calculateExpiry(newToken.expires_in || 3600)
            };
            // Save to localStorage for persistence
            this.saveToStorage();
            // Restart refresh timer
            this.startTokenRefreshTimer();
            // Notify listeners so GlobalStateManager/KeywordsAdapter get the new token
            this.notifyAuthChange(true);
            console.log('[AuthService] Token refreshed successfully - saved to localStorage');
            return this.currentTokens.accessToken;
        }
        catch (error) {
            console.error('[AuthService] Token refresh failed:', error);
            this.logout();
            throw error;
        }
    }
    /**
     * Get current auth header.
     *
     * NOTE: This returns the header even if the token is expired. The caller should
     * handle 401/403 responses and trigger a refresh. This prevents logging out
     * during long-running operations where the refresh timer might not have fired.
     *
     * For operations that need guaranteed fresh tokens, use getAuthHeaderWithRefresh().
     */
    getAuthHeader() {
        if (!this.currentTokens) {
            return null;
        }
        // Log warning but don't logout - let caller handle via authenticatedFetch
        // which has proper 401/403 retry logic
        if (this.isTokenExpired()) {
            console.warn('[AuthService] Token expired, refresh required');
            // Don't auto-logout here - the makeAuthenticatedRequest will handle 401/403
            // and attempt refresh, or the background refresh timer will refresh it
            return null;
        }
        return `Bearer ${this.currentTokens.accessToken}`;
    }
    /**
     * Get current access token
     * Automatically attempts refresh if expired and refresh token is available
     */
    getAccessToken() {
        if (!this.currentTokens) {
            return null;
        }
        // If token is expired but we have a refresh token, try to refresh synchronously
        // Note: This is a sync method but we can't await here, so we return null
        // and rely on the automatic refresh timer to keep tokens fresh
        if (this.isTokenExpired()) {
            console.warn('[AuthService] Token expired in getAccessToken()');
            // Don't auto-logout here - let the refresh timer handle it
            // or let the makeAuthenticatedRequest method handle 401/403
            return null;
        }
        return this.currentTokens.accessToken;
    }
    /**
     * Get current user info
     */
    getCurrentUser() {
        return this.currentUser;
    }
    /**
     * Check if currently authenticated
     */
    isAuthenticated() {
        return !!this.currentTokens && !this.isTokenExpired();
    }
    /**
     * Check if token is expired
     */
    isTokenExpired() {
        if (!this.currentTokens) {
            return true;
        }
        return new Date() >= this.currentTokens.expiresAt;
    }
    /**
     * Calculate token expiry time
     */
    calculateExpiry(expiresIn) {
        return new Date(Date.now() + expiresIn * 1000);
    }
    /**
     * Start automatic token refresh timer
     */
    startTokenRefreshTimer() {
        // Clear existing timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        if (!this.currentTokens) {
            return;
        }
        // Calculate when to refresh (5 min before expiry)
        const refreshTime = this.currentTokens.expiresAt.getTime() - Date.now() - this.tokenRefreshBuffer;
        if (refreshTime > 0) {
            console.log(`[AuthService] Token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`);
            this.refreshTimer = window.setTimeout(async () => {
                try {
                    await this.refreshAccessToken();
                }
                catch (error) {
                    console.error('[AuthService] Auto-refresh failed:', error);
                    this.notifyAuthChange(false);
                }
            }, refreshTime);
        }
    }
    /**
     * Logout and clear all tokens from memory and localStorage
     */
    logout() {
        console.log('[AuthService] Logging out...');
        // Clear timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
        // Clear from memory and localStorage
        this.clearStoredAuth();
        // Double-check: remove any carousel_* keys from localStorage as fallback
        try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.includes('carousel_')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                console.log('[AuthService] Removed carousel key:', key);
            });
        }
        catch (error) {
            console.error('[AuthService] Error during carousel key cleanup:', error);
        }
        // Notify listeners
        this.notifyAuthChange(false);
        console.log('[AuthService] Logout complete - all auth data cleared from localStorage');
    }
    /**
     * Register auth change handler
     * Note: Does NOT immediately call handler - only calls on future changes
     * This prevents initialization loops
     */
    onAuthChange(handler) {
        this.authChangeHandlers.add(handler);
        // Return unsubscribe function
        return () => {
            this.authChangeHandlers.delete(handler);
        };
    }
    /**
     * Notify all handlers of auth state change
     */
    notifyAuthChange(isAuthenticated) {
        console.log('[AuthService] notifyAuthChange called:', {
            isAuthenticated,
            handlerCount: this.authChangeHandlers.size,
            user: this.currentUser?.email
        });
        this.authChangeHandlers.forEach(handler => {
            try {
                console.log('[AuthService] Calling auth change handler...');
                handler(isAuthenticated, this.currentUser || undefined);
            }
            catch (error) {
                console.error('[AuthService] Error in auth change handler:', error);
            }
        });
    }
    /**
     * Make authenticated API request
     * Automatically handles 401/403 by refreshing token and retrying
     */
    async makeAuthenticatedRequest(url, options = {}) {
        const authHeader = this.getAuthHeader();
        if (!authHeader) {
            throw new Error('Not authenticated');
        }
        const headers = {
            ...options.headers,
            'Authorization': authHeader
        };
        const response = await fetch(url, {
            ...options,
            headers
        });
        // Handle 401 (Unauthorized) or 403 (Forbidden) - try refresh once
        if ((response.status === 401 || response.status === 403) && this.currentTokens?.refreshToken) {
            console.log(`[AuthService] Got ${response.status}, attempting token refresh...`);
            try {
                await this.refreshAccessToken();
                // Retry request with new token
                const retryHeaders = {
                    ...options.headers,
                    'Authorization': this.getAuthHeader()
                };
                return fetch(url, {
                    ...options,
                    headers: retryHeaders
                });
            }
            catch (error) {
                console.error(`[AuthService] Token refresh failed on ${response.status}:`, error);
                this.logout();
                throw new Error('Authentication expired, please login again');
            }
        }
        return response;
    }
    /**
     * Get current auth state (for passing between contexts)
     */
    getAuthState() {
        if (!this.currentTokens) {
            return {};
        }
        return {
            tokens: {
                accessToken: this.currentTokens.accessToken,
                refreshToken: this.currentTokens.refreshToken,
                expiresAt: this.currentTokens.expiresAt.toISOString(),
                userEmail: this.currentTokens.userEmail,
            },
            user: this.currentUser,
        };
    }
    /**
     * Hydrate auth state from another context (e.g., dialog -> parent)
     * Saves to localStorage for persistence across add-in close/reopen
     */
    async hydrateAuthState(state) {
        console.log('[AuthService] Hydrating auth state...', {
            hasTokens: !!state?.tokens,
            hasUser: !!state?.user,
            userEmail: state?.user?.email
        });
        if (!state?.tokens) {
            console.log('[AuthService] No tokens in state, skipping hydration');
            return;
        }
        this.currentTokens = {
            accessToken: state.tokens.accessToken,
            refreshToken: state.tokens.refreshToken,
            expiresAt: new Date(state.tokens.expiresAt),
            userEmail: state.tokens.userEmail,
        };
        this.currentUser = state.user ?? null;
        console.log('[AuthService] Auth state hydrated:', {
            email: this.currentUser?.email,
            expiresAt: this.currentTokens.expiresAt
        });
        // Save to localStorage for persistence across add-in close/reopen
        this.saveToStorage();
        // Start refresh timer
        this.startTokenRefreshTimer();
        // Notify listeners
        console.log('[AuthService] Notifying listeners after hydration...');
        this.notifyAuthChange(true);
    }
    /**
     * Check if auth service has been initialized
     */
    isServiceInitialized() {
        return this.isInitialized;
    }
}
// Export singleton instance
export const authService = AuthService.getInstance();
